'use client';

import { PortableText } from '@portabletext/react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import Image, { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { resolvePageLink } from '@/sanity/globals/CTA';
import {
  DEFAULT_LANGUAGE,
  resolveI18nRichtext,
  resolveI18nText,
} from '@/sanity/globals/I18n';
import { RIGHTS_RESERVED } from '@/components/global/partials/Footer';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';
import { HalftoneTiles } from './textures';

import logo from '@/public/logo-black.svg';

/**
 * Figma: desktop 7205:2493, mobile 8662:24870. This page ships its own footer;
 * the legal links and the disclaimer come from the global footer document.
 */
const LEGAL_LINK_COUNT = 4;

export default function PageFooter({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  const language = data?.settings?.language || DEFAULT_LANGUAGE;
  const footer = data?.settings?.footer;

  const policyColumn = footer?.navColumns?.find((column) =>
    /policy/i.test(column?.heading ?? ''),
  );
  const legalLinks = (policyColumn?.links ?? []).slice(0, LEGAL_LINK_COUNT);
  const disclaimer = footer
    ? resolveI18nRichtext(footer.disclaimer, footer.disclaimerI18n, language)
    : undefined;

  const hasCta =
    data?.finalChip || data?.finalTitle || data?.finalText || data?.finalCTA;

  return (
    <footer
      className={cn(
        'relative flex flex-col items-center max-lg:gap-0 lg:gap-[3rem] w-full overflow-clip bg-[#F4F4F4] max-lg:pt-[5.625rem] lg:pt-[12.5rem]',
        className,
      )}
    >
      <HalftoneTiles
        tiles={['left-[-1.375rem] max-sm:hidden', 'right-[-1.293rem] max-sm:hidden']}
        width="max(121.334rem, 50.556vw)"
        height="max(124.875rem, 52.031vw)"
        opacity={0.1}
      />
      <HalftoneTiles
        tiles={['sm:hidden right-[0.1175rem]']}
        width="53.632rem"
        height="55.198rem"
        opacity={0.1}
      />
      <HalftoneTiles
        tiles={['sm:hidden left-[0.415rem] top-[72.143rem]']}
        width="51.237rem"
        height="52.732rem"
        opacity={0.1}
      />

      {hasCta && (
        <section
          id="chapter-8"
          data-chapter
          className="relative flex flex-col items-center max-lg:gap-[2.75rem] lg:gap-[3.5rem] w-full px-[2rem] lg:px-0"
        >
          <div className="flex flex-col items-center max-lg:gap-[2rem] lg:gap-[2.5rem] w-full">
            <div className="flex flex-col items-center justify-center gap-[1.25rem] w-[min(106rem,100%)]">
              {data?.finalChip && (
                <p className="mb-0 font-blacker italic text-[1.875rem] leading-[1.5] text-black text-center whitespace-nowrap">
                  {data.finalChip}
                </p>
              )}
              {data?.finalTitle && (
                <div
                  className="richtext text-center w-full
                  prose-p:font-geist max-lg:prose-p:font-medium lg:prose-p:font-semibold max-lg:prose-p:text-[5rem] lg:prose-p:text-[7rem] max-lg:prose-p:leading-[1.08] lg:prose-p:leading-none max-lg:prose-p:tracking-[-0.25rem] prose-p:text-[#2E3436] prose-p:mb-0
                  max-lg:[&_p_span]:leading-[0]
                  [&_span]:font-normal [&_span]:italic [&_span]:!text-[#376E5E] [&_span]:![background:linear-gradient(to_top,#D4E8E3_59%,transparent_59%)]"
                >
                  <PortableText
                    value={data.finalTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {data?.finalText && (
              <div
                className="richtext text-center w-full
                prose-p:font-geist prose-p:font-normal prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black prose-p:opacity-80 max-lg:prose-p:mb-[2rem] lg:prose-p:mb-[1.5rem]
                lg:[&_p:nth-child(2)]:max-w-[87.5rem] lg:[&_p:nth-child(3)]:max-w-[60.75rem]
                max-lg:[&_p:nth-child(1)]:max-w-[35.25rem] max-lg:[&_p:nth-child(2)]:max-w-[45rem] max-lg:[&_p:nth-child(3)]:max-w-[45rem]"
              >
                <PortableText
                  value={data.finalText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.finalCTA && (
            <CTAButton
              data={data.finalCTA}
              overrideVariant="default"
              className="max-sm:!w-full sm:!w-auto justify-center
              max-sm:[&_.button]:!w-full sm:[&_.button]:!w-[27.125rem] [&_.button]:!min-h-[5.875rem] [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem]
              [&_.button]:!text-[1.75rem] [&_.button]:!tracking-[-0.035rem]"
            />
          )}
        </section>
      )}

      {data?.finalImage && (
        <div className="relative w-[min(150.5rem,100%)] max-lg:h-[39.641rem] lg:h-[59.25rem] shrink-0">
          <div className="absolute max-lg:left-[calc(50%_-_1.978rem)] max-lg:-translate-x-1/2 lg:left-[42.5rem] max-lg:top-[0.928rem] lg:top-[-2.641rem] max-lg:w-[38.146rem] max-lg:h-[41.75rem] lg:w-[54.25rem] lg:h-[59.375rem] pointer-events-none mix-blend-darken">
            <GroqImage
              src={data.finalImage}
              alt=""
              quality={100}
              className="!w-full !h-full object-contain"
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <div
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 max-sm:w-[51.625rem] sm:w-screen sm:min-w-[240rem] max-lg:h-[19.25rem] lg:h-[22.25rem] max-lg:top-[23.516rem] lg:top-[37.61rem] bg-gradient-to-b from-[#F4F4F4]/0 to-[#F4F4F4] max-lg:backdrop-blur-[1.368rem] lg:backdrop-blur-[1.9375rem]"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent, #000)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000)',
            }}
          />
        </div>
      )}

      <div className="relative flex flex-col items-center gap-[3rem] w-full px-[2rem] lg:px-0 max-lg:pb-[5.625rem] lg:pb-[4rem]">
        <div className="flex flex-col max-lg:gap-[2.25rem] lg:gap-[3rem] max-lg:w-[min(45.9375rem,100%)] lg:w-[175rem]">
          <div className="flex flex-col lg:flex-row items-center justify-between max-lg:gap-[2.25rem] lg:gap-[2rem] w-full">
            <p className="mb-0 max-lg:order-last flex-1 font-geist text-[2rem] leading-[3rem] text-black opacity-[0.79] text-center lg:text-left">
              © IQ Capital - {RIGHTS_RESERVED[language] ?? RIGHTS_RESERVED.en}
            </p>
            <Image
              src={logo}
              alt="IQ Capital"
              className="max-lg:!w-[18.25rem] max-lg:!h-[3.38rem] lg:!w-[19.375rem] lg:!h-[3.589rem] shrink-0"
              style={{ width: '19.375rem', height: '3.589rem' }}
            />
            <p className="mb-0 flex-1 font-geist text-[2rem] leading-[3rem] text-black opacity-[0.79] text-center lg:text-right">
              {legalLinks.map((item, index) => {
                const href = resolvePageLink(item.link, language) ?? '#';
                const isExternal =
                  item.link?.externalLink && item.link?.openInNewTab;
                return (
                  <span key={item?.title ?? index}>
                    {index > 0 && ' / '}
                    <Link
                      href={href}
                      target={isExternal ? '_blank' : '_self'}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className="no-underline hover:underline"
                    >
                      {resolveI18nText(item.title, item.i18n, language)}
                    </Link>
                  </span>
                );
              })}
            </p>
          </div>

          {disclaimer && (
            <div
              className="richtext w-full max-w-none border-t max-lg:border-black/10 lg:border-black/20 max-lg:pt-[2.25rem] max-lg:pb-0 max-lg:px-[0.969rem] lg:py-[4rem] text-center
              prose-p:font-geist prose-p:text-[1.75rem] prose-p:leading-[1.5] prose-p:text-black prose-p:opacity-50 prose-p:mb-[2.625rem]"
            >
              <PortableText value={disclaimer} components={richTextComponent} />
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
